import { useState, useEffect } from "react";
import { api, formatSpanishDate, formatTime } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import EmailTextsPanel from "@/components/EmailTextsPanel";
import { Lock, LogOut, CalendarCheck, Users, Settings, Link as LinkIcon, Unlink, RefreshCw, MessageCircle, X, Phone, PenLine, Eye, Save, Trash2, AlertTriangle, StickyNote, Check, Mail } from "lucide-react";

const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

export default function Admin() {
  const [logged, setLogged] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [me, setMe] = useState(null);
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [waitlist, setWaitlist] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState(null);
  const [chatSel, setChatSel] = useState(null);
  // Ronda 33.13 · historial del bot de reservas (separado del de Sol).
  const [bookingChatOpen, setBookingChatOpen] = useState(false);
  const [bookingChatHistory, setBookingChatHistory] = useState(null);
  const [bookingChatSel, setBookingChatSel] = useState(null);
  const [chatsTab, setChatsTab] = useState("sol");
  useEffect(() => {
    if (tab !== "chats") { return; }
    if (chatsTab === "sol") {
      api.get("/admin/chat-history").then(({ data }) => setChatHistory(data)).catch(() => { toast.error("No pude cargar las conversaciones con Sol"); setChatHistory([]); });
    } else {
      api.get("/admin/booking-chat-history").then(({ data }) => setBookingChatHistory(data)).catch(() => { toast.error("No pude cargar las conversaciones de reservas"); setBookingChatHistory([]); });
    }
  }, [tab, chatsTab]);
  const [selBookingChat, setSelBookingChat] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [notesTarget, setNotesTarget] = useState(null);
  // Ronda 31: selecciones para borrado masivo. Un Set por tab evita
  // contaminaciones cruzadas si la usuaria salta entre pestañas.
  const [selBookings, setSelBookings] = useState(() => new Set());
  const [selClients, setSelClients] = useState(() => new Set());
  const [selChat, setSelChat] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Ronda 33.14 · Reservas fallidas (calendar insert falló).
  const [failedBookings, setFailedBookings] = useState([]);
  // Ronda 33.19 · Registro de emails enviados a clientas.
  const [emails, setEmails] = useState([]);
  const [emailStartDate, setEmailStartDate] = useState("");
  const [selEmail, setSelEmail] = useState(() => new Set());
  const [viewEmail, setViewEmail] = useState(null); // {id, html, subject, ...}

  const toggleSel = (setter) => (id) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelBooking = toggleSel(setSelBookings);
  const toggleSelClient = toggleSel(setSelClients);
  const toggleSelChat = toggleSel(setSelChat);

  // The auth token lives in an httpOnly cookie set by /admin/login. On mount
  // we just ask the backend "am I authenticated?" — no client-side inspection.
  useEffect(() => {
    (async () => {
      try {
        await api.get("/admin/me");
        setLogged(true);
      } catch {
        setLogged(false);
      } finally {
        setChecking(false);
      }
    })();
    const onUnauth = () => setLogged(false);
    window.addEventListener("bea:admin-unauthenticated", onUnauth);
    return () => window.removeEventListener("bea:admin-unauthenticated", onUnauth);
  }, []);

  useEffect(() => {
    if (logged) refreshAll();
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") {
      toast.success("Google Calendar conectado.");
      window.history.replaceState({}, "", "/admin");
    }
  }, [logged]);

  const [lastRefresh, setLastRefresh] = useState(null);
  const refreshAll = async () => {
    try {
      const [m, c, w, b] = await Promise.all([
        api.get("/admin/me"),
        api.get("/admin/config"),
        api.get("/admin/waitlist"),
        api.get("/admin/bookings"),
      ]);
      setMe(m.data);
      setConfig(c.data);
      setWaitlist(w.data);
      setBookings(b.data);
      setLastRefresh(new Date());
      // Ronda 33.14 · cargar también las reservas fallidas. Errores no
      // bloqueantes: si el endpoint falla, la pestaña simplemente estará vacía.
      try {
        const f = await api.get("/admin/bookings/failed");
        setFailedBookings(f.data || []);
      } catch (err) {
        console.warn("Admin: no pude cargar failed_bookings", err);
      }
      // Ronda 33.19 · cargar registro de emails
      try {
        const em = await api.get("/admin/emails");
        setEmails(em.data?.items || []);
        setEmailStartDate(em.data?.start_date || "");
      } catch (err) {
        console.warn("Admin: no pude cargar email_logs", err);
      }
      if (m.data.google_connected) {
        try {
          const { data } = await api.get("/admin/calendars");
          setCalendars(data);
        } catch (err) {
          // Non-critical: calendars list is only used for the dropdown. If the
          // fetch fails (network / permissions), the panel still works with
          // the primary calendar as fallback. Log for observability.
          console.warn("Admin: no se pudieron cargar los calendarios de Google", err);
        }
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        setLogged(false);
      }
    }
  };

  const login = async () => {
    try {
      await api.post("/admin/login", { password });
      setLogged(true);
      toast.success("Bienvenida Bea");
    } catch (e) {
      toast.error("Contraseña incorrecta");
    }
  };

  const logout = async () => {
    // Ronda 31.7: si /admin/logout falla (cookie ya expirada, red caída),
    // seguimos limpiando el estado local para que la UI vuelva al login.
    try { await api.post("/admin/logout"); } catch (err) {
      console.warn("Admin.logout: llamada al backend falló, limpiando estado local igualmente", err);
    }
    setLogged(false);
    setPassword("");
  };

  const saveConfig = async (patch) => {
    try {
      const { data } = await api.put("/admin/config", patch);
      setConfig(data);
      toast.success("Guardado");
    } catch { toast.error("Error guardando"); }
  };

  // Ronda 31: bulk delete helpers. Un solo hit al backend por operación.
  const bulkDeleteBookings = async (ids, source) => {
    if (!ids?.length) return;
    if (!window.confirm(`¿Eliminar ${ids.length} reserva(s) de forma permanente? Se borran del calendario y del registro. Sin email a la clienta.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/bookings/bulk-delete", { ids });
      toast.success(`${data.deleted} reserva(s) eliminadas`);
      if (source === "bookings") setSelBookings(new Set());
      if (source === "clients") setSelClients(new Set());
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error borrando en bloque");
    }
    setBulkBusy(false);
  };

  const bulkDeleteChat = async (keys) => {
    if (!keys?.length) return;
    if (!window.confirm(`¿Eliminar ${keys.length} conversación(es) del historial? No se puede deshacer.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/chat-history/bulk-delete", { keys });
      toast.success(`${data.deleted} mensajes borrados`);
      setSelChat(new Set());
      // Refresh chat history
      try {
        const res = await api.get("/admin/chat-history");
        setChatHistory(res.data);
        if (chatSel && keys.includes(chatSel.key)) setChatSel(null);
      } catch (err) {
        // El bulk-delete YA se ejecutó y el toast de éxito se mostró.
        // Este catch cubre solo la recarga posterior de la lista — si falla,
        // el toast lo indica y la usuaria puede refrescar manualmente.
        console.warn("bulkDeleteChat: no se pudo recargar el historial tras el borrado", err);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error borrando en bloque");
    }
    setBulkBusy(false);
  };

  // Ronda 33.13 · Bulk-delete separado para el historial del bot de reservas.
  const bulkDeleteBookingChat = async (keys) => {
    if (!keys?.length) return;
    if (!window.confirm(`¿Eliminar ${keys.length} conversación(es) del bot de reservas? No se puede deshacer.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/booking-chat-history/bulk-delete", { keys });
      toast.success(`${data.deleted} mensajes borrados`);
      setSelBookingChat(new Set());
      try {
        const res = await api.get("/admin/booking-chat-history");
        setBookingChatHistory(res.data);
        if (bookingChatSel && keys.includes(bookingChatSel.key)) setBookingChatSel(null);
      } catch (err) {
        console.warn("bulkDeleteBookingChat: no pude recargar el historial", err);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error borrando en bloque");
    }
    setBulkBusy(false);
  };

  const toggleSelBookingChat = (k) => setSelBookingChat((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const connectGoogle = () => {
    // Cookie travels automatically with the browser navigation.
    // Use `window.location.origin` so the OAuth callback loops back to the
    // exact domain the admin is browsing from (works across preview, prod,
    // and any custom domain without needing separate env vars per env).
    window.location.href = `${window.location.origin}/api/oauth/google/start`;
  };

  const disconnectGoogle = async () => {
    await api.post("/admin/google/disconnect");
    toast.success("Google desconectado");
    refreshAll();
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-[#6B6B6B] text-sm animate-pulse">Comprobando sesión…</div>
      </div>
    );
  }
  if (!logged) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card max-w-md w-full">
          <Lock className="gold" size={24}/>
          <h1 className="font-serif text-3xl font-light mt-4">Panel de Bea</h1>
          <p className="text-[#6B6B6B] mt-2 text-sm">Acceso restringido.</p>
          <input
            type="password"
            className="field mt-6"
            data-testid="admin-password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
          />
          <button className="btn-primary mt-4 w-full" data-testid="admin-login-btn" onClick={login}>Entrar</button>
          <ForgotPasswordLink />
        </motion.div>
      </div>
    );
  }

  return (
    <div>
      <header className="glass sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-serif text-2xl gold">Panel · Bea</div>
          <div className="flex items-center gap-2">
            {lastRefresh && (
              <span className="text-[10px] text-[#8A8175] hidden sm:inline" data-testid="admin-last-refresh" title="Hora de la última recarga de datos">
                Actualizado {lastRefresh.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
            <button onClick={refreshAll} className="btn-ghost text-sm" data-testid="admin-refresh"><RefreshCw size={14} className="inline mr-1"/>Refrescar</button>
            <button onClick={logout} className="btn-ghost text-sm" data-testid="admin-logout"><LogOut size={14} className="inline mr-1"/>Salir</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Google connection banner */}
        {me && !me.google_connected && (
          <div className="card border-yellow-500/40 mb-6" data-testid="google-connect-banner">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-serif text-xl">Conecta tu Google Calendar</div>
                <p className="text-[#6B6B6B] text-sm mt-1">Sin esto la app no puede crear ni leer citas.</p>
              </div>
              <button onClick={connectGoogle} className="btn-primary" data-testid="connect-google-btn"><LinkIcon size={14} className="inline mr-1"/>Conectar Google</button>
            </div>
          </div>
        )}
        {me?.google_connected && (
          <div className="card mb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="label-eyebrow">Google conectado</div>
                <div className="mt-2 text-sm">{me.google_email}</div>
                <div className="mt-3">
                  <label className="text-xs text-[#6B6B6B]">Calendario</label>
                  <select
                    data-testid="calendar-select"
                    className="field mt-1"
                    value={config?.calendar_id || "primary"}
                    onChange={(e) => saveConfig({ calendar_id: e.target.value })}
                  >
                    {calendars.length === 0 && <option value="primary">primary</option>}
                    {calendars.map((c) => <option key={c.id} value={c.id}>{c.summary}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={disconnectGoogle} className="btn-ghost" data-testid="disconnect-google-btn"><Unlink size={14} className="inline mr-1"/>Desconectar</button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { k: "dashboard", label: "Resumen", icon: CalendarCheck },
            { k: "schedule", label: "Horarios", icon: Settings },
            { k: "waitlist", label: `Lista espera (${waitlist.length})`, icon: Users },
            { k: "bookings", label: `Justificantes${bookings.filter(b=>b.status==='awaiting_review').length ? ` (${bookings.filter(b=>b.status==='awaiting_review').length} pdte.)` : ''}`, icon: CalendarCheck },
            { k: "failed", label: `Fallidas${failedBookings.length ? ` (${failedBookings.length})` : ''}`, icon: AlertTriangle },
            { k: "emails", label: `Emails${emails.length ? ` (${emails.length})` : ''}`, icon: Mail },
            { k: "chats", label: "Conversaciones", icon: MessageCircle },
            { k: "emailtexts", label: "Textos de emails", icon: PenLine },
            { k: "clients", label: "Clientas", icon: Users },
            { k: "content", label: "Textos web", icon: PenLine },
            { k: "payment", label: "Pago", icon: Settings },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => { setTab(t.k); if (typeof refreshAll === "function") refreshAll(); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.k ? "bg-[#B8912E] text-white shadow-md" : "border border-[#0A0A0A]/20 text-[#0A0A0A] bg-white hover:border-[#B8912E] hover:bg-[#FFF9EB]"}`}
            >
              <t.icon size={14} className="inline mr-1"/>{t.label}
            </button>
          ))}
        </div>

        {tab === "emailtexts" && <EmailTextsPanel />}

        {tab === "chats" && (
          <div className="flex flex-wrap gap-2" data-testid="chats-subtabs">
            <button onClick={() => setChatsTab("sol")} data-testid="chats-tab-sol"
              className={`px-4 py-2 rounded-full text-sm font-medium ${chatsTab === "sol" ? "bg-[#B8912E] text-white" : "border border-[#0A0A0A]/20 bg-white"}`}>
              Conversaciones con Sol
            </button>
            <button onClick={() => setChatsTab("booking")} data-testid="chats-tab-booking"
              className={`px-4 py-2 rounded-full text-sm font-medium ${chatsTab === "booking" ? "bg-[#3F6B3A] text-white" : "border border-[#0A0A0A]/20 bg-white"}`}>
              Conversaciones de reservas
            </button>
          </div>
        )}

        {tab === "dashboard" && config && (
          <div className="grid md:grid-cols-3 gap-6">
            <button
              type="button"
              onClick={() => { setChatsTab("sol"); setTab("chats"); }}
              className="card text-left transition-transform hover:-translate-y-0.5 hover:border-[#B8912E]/50"
              data-testid="chat-stats-card"
            >
              <div className="label-eyebrow flex items-center gap-2">
                <MessageCircle size={12} className="text-[#B8912E]"/> Chat este mes
              </div>
              <div className="mt-4 font-serif text-4xl gold" data-testid="chat-stats-value">{me?.chat_questions_this_month ?? "—"}</div>
              <div className="mt-2 text-sm text-[#6B6B6B]">preguntas atendidas — pulsa para ver el historial</div>
            </button>
            <button
              type="button"
              onClick={() => { setChatsTab("booking"); setTab("chats"); }}
              className="card text-left transition-transform hover:-translate-y-0.5 hover:border-[#B8912E]/50"
              data-testid="booking-chat-stats-card"
            >
              <div className="label-eyebrow flex items-center gap-2">
                <MessageCircle size={12} className="text-[#B8912E]"/> Bot de reservas
              </div>
              <div className="mt-4 font-serif text-3xl gold">Historial</div>
              <div className="mt-2 text-sm text-[#6B6B6B]">conversaciones del bot que gestiona fecha y hora — pulsa para revisarlas</div>
            </button>
            <div className="card">
              <div className="label-eyebrow">Modo vacaciones</div>
              <div className="mt-4 flex items-center justify-between">
                <span className="font-serif text-2xl">{config.vacation_mode ? "Activado" : "Desactivado"}</span>
                <button
                  data-testid="vacation-toggle"
                  className={`w-14 h-8 rounded-full relative transition-colors ${config.vacation_mode ? "bg-[#B8912E]" : "bg-white/10"}`}
                  onClick={() => saveConfig({ vacation_mode: !config.vacation_mode })}
                >
                  <div className={`absolute top-1 w-6 h-6 rounded-full bg-black transition-all ${config.vacation_mode ? "left-7" : "left-1"}`}></div>
                </button>
              </div>
              {config.vacation_mode && (
                <textarea
                  className="field mt-4 text-sm"
                  rows={3}
                  data-testid="vacation-message"
                  defaultValue={config.vacation_message}
                  onBlur={(e) => saveConfig({ vacation_message: e.target.value })}
                />
              )}
            </div>
            <div className="card">
              <div className="label-eyebrow">Precio consulta</div>
              <div className="mt-4 flex items-baseline gap-2">
                <input type="number" className="field text-2xl font-serif" style={{maxWidth:"120px"}}
                  data-testid="price-input"
                  defaultValue={config.consultation_price_eur}
                  onBlur={(e) => saveConfig({ consultation_price_eur: parseInt(e.target.value) || 75 })}
                />
                <span className="text-[#6B6B6B]">€</span>
              </div>
            </div>
            <div className="card">
              <div className="label-eyebrow">Teléfono urgencias</div>
              <input className="field mt-4" data-testid="emergency-input" defaultValue={config.emergency_phone}
                onBlur={(e) => saveConfig({ emergency_phone: e.target.value })}/>
            </div>
            <div className="card">
              <div className="label-eyebrow">Consulta urgente / equipo de confianza</div>
              <p className="text-[#6B6B6B] text-sm mt-2">
                Se muestra como tarjeta destacada en la web principal y en la reserva.
                Ideal para cuando estás de vacaciones o no hay hueco cercano y quieres derivar a un equipo de confianza.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <button
                  data-testid="urgency-toggle"
                  className={`w-14 h-8 rounded-full relative transition-colors ${config.urgency_enabled ? "bg-[#B8912E]" : "bg-[#0A0A0A]/15"}`}
                  onClick={() => saveConfig({ urgency_enabled: !config.urgency_enabled })}
                >
                  <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${config.urgency_enabled ? "left-7" : "left-1"}`}></div>
                </button>
                <span className="text-sm text-[#6B6B6B]">{config.urgency_enabled ? "Activado" : "Desactivado"}</span>
              </div>
              <div className="mt-4 p-4 rounded-xl border border-[#B8912E]/25 bg-[#FFF9EB] text-sm text-[#0A0A0A] leading-relaxed" data-testid="urgency-editor-hint">
                Para editar el <strong>título</strong> y el <strong>texto del modal</strong> de esta tarjeta, ve a la pestaña <strong>«Textos web» → Consulta urgente</strong>. Los cambios se guardan en un solo sitio y se ven al momento en la web.
              </div>
            </div>
          </div>
        )}

        {tab === "schedule" && config && (
          <div className="space-y-4">
            <div className="card">
              <div className="label-eyebrow">Duración de cada cita</div>
              <div className="mt-3">
                <input type="number" className="field" style={{maxWidth:"140px"}} data-testid="slot-minutes"
                  defaultValue={config.slot_minutes}
                  onBlur={(e) => saveConfig({ slot_minutes: parseInt(e.target.value) || 60 })}/>
                <span className="ml-2 text-sm text-[#6B6B6B]">minutos</span>
              </div>
            </div>
            <PersonalCalendarPicker
              calendars={calendars}
              consultasId={config.calendar_id}
              value={config.personal_calendar_id || ""}
              onChange={(id) => saveConfig({ personal_calendar_id: id || null })}
            />
            {DAYS.map((day, idx) => (
              <ScheduleDayEditor
                key={idx}
                day={day}
                idx={idx}
                ranges={config.schedule[String(idx)] || []}
                onChange={(newRanges) => {
                  const s = { ...config.schedule, [String(idx)]: newRanges };
                  saveConfig({ schedule: s });
                }}
              />
            ))}
          </div>
        )}

        {tab === "waitlist" && (
          <div className="card">
            <div className="mb-4 p-3 rounded-xl bg-[#FFF9EB] border border-[#B8912E]/25 text-xs text-[#0A0A0A] leading-relaxed" data-testid="waitlist-help">
              <strong>Cómo funciona esta lista:</strong> las clientas se apuntan aquí durante el modo vacaciones.
              Cuando <em>desactivas</em> el modo vacaciones desde «Resumen», el sistema envía automáticamente un email
              a TODAS las clientas por orden de llegada (Ronda 26).
              El botón <strong>«Marcar avisada»</strong> de cada fila es solo una marca de seguimiento interno para ti:
              <strong> no envía ningún email</strong>. Úsalo cuando ya hayas avisado tú manualmente por WhatsApp.
            </div>
            {waitlist.length === 0 ? <p className="text-[#6B6B6B]">No hay nadie en lista de espera.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[#6B6B6B]"><th className="pb-3">Orden</th><th className="pb-3">Nombre</th><th className="pb-3">Teléfono</th><th className="pb-3">Email</th><th className="pb-3">Apuntada</th><th className="pb-3">Estado</th><th></th></tr></thead>
                <tbody>
                  {waitlist.map((w, i) => (
                    <tr key={w.id} className="border-t border-white/5" data-testid={`waitlist-row-${w.phone}`}>
                      <td className="py-3 gold font-serif text-lg">#{i + 1}</td>
                      <td className="py-3">{w.name || <span className="text-[#6B6B6B]">—</span>}</td>
                      <td className="py-3">{w.phone}</td>
                      <td className="py-3 text-[#6B6B6B]">{w.email ? <a href={`mailto:${w.email}`} className="gold underline">{w.email}</a> : "—"}</td>
                      <td className="py-3 text-[#6B6B6B]">{new Date(w.created_at).toLocaleString("es-ES")}</td>
                      <td className="py-3">{w.status === "notified" ? <span className="text-green-400">Avisada</span> : "Esperando"}</td>
                      <td className="py-3 text-right space-x-2">
                        <a href={`https://wa.me/${w.phone.replace('+','')}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid={`waitlist-whatsapp-${w.phone}`}>WhatsApp</a>
                      <button onClick={() => api.post(`/admin/waitlist/${w.id}/notified`).then(refreshAll)} className="btn-ghost text-xs" title="Marca interna: no envía email. Anota que ya has avisado a esta clienta (típicamente por WhatsApp).">Marcar avisada</button>
                        <button onClick={() => api.delete(`/admin/waitlist/${w.id}`).then(refreshAll)} className="btn-ghost text-xs">Quitar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "bookings" && (
          <div className="space-y-6">
            {(() => {
              const toReview = bookings
                .filter(b => b.status === "awaiting_review")
                .sort((a, b) => {
                  // oldest first (order of reception)
                  const ta = a.receipt_uploaded_at || a.created_at || "";
                  const tb = b.receipt_uploaded_at || b.created_at || "";
                  return ta.localeCompare(tb);
                });
              return toReview.length > 0 ? (
                <div className="card border-[#B8912E]/30" data-testid="review-queue">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="label-eyebrow">Justificantes de pago ({toReview.length})</div>
                      <p className="text-[#6B6B6B] text-sm mt-2">Ordenados por orden de recepción. Revisa cada uno y confirma o rechaza.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-[#6B6B6B] flex items-center gap-2 cursor-pointer" data-testid="review-select-all">
                        <input
                          type="checkbox"
                          checked={toReview.length > 0 && toReview.every(b => selBookings.has(b.id))}
                          onChange={(e) => {
                            if (e.target.checked) setSelBookings(new Set(toReview.map(b => b.id)));
                            else setSelBookings(prev => {
                              const next = new Set(prev);
                              toReview.forEach(b => next.delete(b.id));
                              return next;
                            });
                          }}
                        />
                        Seleccionar todo
                      </label>
                      <button
                        onClick={() => bulkDeleteBookings(toReview.filter(b => selBookings.has(b.id)).map(b => b.id), "bookings")}
                        disabled={bulkBusy || toReview.every(b => !selBookings.has(b.id))}
                        data-testid="review-bulk-delete"
                        className="btn-ghost text-xs text-red-600 hover:text-red-800 disabled:opacity-40"
                      >
                        <Trash2 size={12} className="inline mr-1"/>Eliminar seleccionadas ({toReview.filter(b => selBookings.has(b.id)).length})
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 space-y-3">
                    {toReview.map((b) => {
                      // Receipt URL: cookie authenticates the request automatically.
                      // Use window.location.origin so the <img src> resolves
                      // to the same domain the admin is browsing from (works
                      // across preview/prod/custom domains without touching
                      // env vars).
                      const receiptSrc = b.has_receipt
                        ? `${window.location.origin}/api/receipt/${b.id}`
                        : null;
                      return (
                      <div key={b.id} className="p-4 rounded-xl border border-[#B8912E]/25 bg-white" data-testid={`review-row-${b.phone}`}>
                        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              className="mt-2"
                              checked={selBookings.has(b.id)}
                              onChange={() => toggleSelBooking(b.id)}
                              data-testid={`review-check-${b.phone}`}
                            />
                            <div>
                              <div className="font-serif text-lg text-[#0A0A0A]">{b.name || "Sin nombre"} · {b.start ? `${formatSpanishDate(b.start)} · ${formatTime(b.start)}` : "-"}</div>
                              <div className="text-sm text-[#6B6B6B] mt-1">{b.phone}{b.client_email ? ` · ${b.client_email}` : ""}</div>
                              {b.bea_call_requested && (
                                <div
                                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FEF3C7] border border-[#F59E0B] text-[#78350F] text-xs font-semibold"
                                  data-testid={`bea-call-badge-${b.phone}`}
                                  title="La clienta ha pedido explícitamente que Bea la llame"
                                >
                                  <Phone size={11}/> Bea llama a la clienta
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 w-full sm:w-auto">
                            <button
                              onClick={async () => {
                                if (!window.confirm("¿Confirmar pago y enviar email de confirmación a la clienta?")) return;
                                try {
                                  await api.post(`/admin/bookings/${b.id}/approve`);
                                  toast.success("Pago confirmado, email enviado a la clienta.");
                                  refreshAll();
                                } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
                              }}
                              className="btn-primary text-sm w-full sm:w-auto justify-center flex items-center gap-2 py-3"
                              data-testid={`approve-${b.phone}`}
                            ><Check size={16}/> Confirmar pago</button>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                const res = await api.get(`/receipt/${b.id}`, { responseType: "blob" });
                                window.open(URL.createObjectURL(res.data));
                              }}
                              className="btn-ghost text-xs w-full sm:w-auto justify-center flex items-center gap-2"
                              data-testid={`view-receipt-${b.phone}`}
                            >Abrir justificante</button>
                            <button
                              onClick={async () => {
                                const reason = window.prompt("Motivo (opcional) para la clienta:");
                                if (reason === null) return; // cancelled
                                try {
                                  await api.post(`/admin/bookings/${b.id}/reject`, { reason });
                                  toast.success("Reserva rechazada, cita eliminada del calendario.");
                                  refreshAll();
                                } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
                              }}
                              className="btn-ghost text-xs text-red-500 w-full sm:w-auto justify-center flex items-center gap-2"
                              data-testid={`reject-${b.phone}`}
                            >Rechazar</button>
                            <CancelBookingButton b={b} onDone={refreshAll} wide />
                            <button
                              onClick={async () => {
                                if (!window.confirm(`¿Eliminar permanentemente esta reserva de ${b.name || b.phone}? La cita se borra del calendario y del registro. Úsalo para pruebas o duplicados; no envía email a la clienta.`)) return;
                                try {
                                  await api.delete(`/admin/bookings/${b.id}`);
                                  toast.success("Reserva eliminada.");
                                  refreshAll();
                                } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
                              }}
                              className="btn-ghost text-xs text-red-500 hover:text-red-700 w-full sm:w-auto justify-center flex items-center gap-2"
                              title="Eliminar sin avisar a la clienta"
                              data-testid={`delete-review-${b.phone}`}
                            ><Trash2 size={12}/>Eliminar</button>
                          </div>
                        </div>

                        {(b.client_notes || []).length > 0 && (
                          <div className="mt-3 p-3 rounded-lg bg-[#FFF9EB] border border-[#B8912E]/30" data-testid={`review-notes-${b.phone}`}>
                            <div className="flex items-center gap-2 text-xs font-semibold text-[#8F6F1F] mb-2">
                              <StickyNote size={12}/> Notas de la clienta ({(b.client_notes || []).length})
                            </div>
                            <div className="space-y-2">
                              {(b.client_notes || []).map((n, i) => (
                                <div key={i} className="text-sm text-[#0A0A0A] leading-relaxed">
                                  <div className="text-[10px] text-[#6B6B6B] mb-0.5">{n.created_at ? new Date(n.created_at).toLocaleString("es-ES") : ""}</div>
                                  <div className="whitespace-pre-line italic">«{n.note}»</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {receiptSrc && (
                          <div className="mt-4">
                            <div className="text-xs text-[#6B6B6B] mb-2">Justificante subido:</div>
                            <a href={receiptSrc} target="_blank" rel="noreferrer" data-testid={`receipt-preview-link-${b.phone}`}>
                              <img
                                src={receiptSrc}
                                alt={`Justificante de ${b.name || b.phone}`}
                                data-testid={`receipt-preview-${b.phone}`}
                                className="rounded-lg border border-white/10 max-h-72 w-auto bg-white p-1"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            </a>
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </div>
                </div>
              ) : null;
            })()}

            <div className="card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="label-eyebrow">Todas las reservas</div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#6B6B6B] flex items-center gap-2 cursor-pointer" data-testid="bookings-select-all">
                    <input
                      type="checkbox"
                      checked={bookings.length > 0 && bookings.every(b => selBookings.has(b.id))}
                      onChange={(e) => {
                        if (e.target.checked) setSelBookings(new Set(bookings.map(b => b.id)));
                        else setSelBookings(new Set());
                      }}
                    />
                    Seleccionar todo ({bookings.length})
                  </label>
                  <button
                    onClick={() => bulkDeleteBookings(Array.from(selBookings), "bookings")}
                    disabled={bulkBusy || selBookings.size === 0}
                    data-testid="bookings-bulk-delete"
                    className="btn-ghost text-xs text-red-600 hover:text-red-800 disabled:opacity-40"
                  >
                    <Trash2 size={12} className="inline mr-1"/>Eliminar seleccionadas ({selBookings.size})
                  </button>
                </div>
              </div>
              {bookings.length === 0 ? <p className="text-[#6B6B6B] mt-4">Sin reservas todavía.</p> : (
                <table className="w-full text-sm mt-4">
                  <thead><tr className="text-left text-[#6B6B6B]"><th className="pb-3 w-8"></th><th className="pb-3">Nombre</th><th className="pb-3">Cita</th><th className="pb-3">Teléfono</th><th className="pb-3">Estado</th><th className="pb-3">Justificante</th><th className="pb-3 text-right">Acciones</th></tr></thead>
                  <tbody>
                    {(() => {
                      // Ronda 25: pendientes de confirmar arriba, por orden de
                      // llegada (created_at ASC). Confirmadas/canceladas/etc.
                      // debajo, por fecha de creación descendente (más
                      // recientes primero dentro de ese grupo).
                      const isPending = (b) => b.status === "pending" || b.status === "awaiting_review";
                      const sorted = bookings.slice().sort((a, b) => {
                        const pa = isPending(a) ? 0 : 1;
                        const pb = isPending(b) ? 0 : 1;
                        if (pa !== pb) return pa - pb;
                        const ta = a.created_at || "";
                        const tb = b.created_at || "";
                        return isPending(a) ? ta.localeCompare(tb) : tb.localeCompare(ta);
                      });
                      const firstNonPendingIdx = sorted.findIndex((b) => !isPending(b));
                      const rows = [];
                      sorted.forEach((b, idx) => {
                        if (idx === firstNonPendingIdx && firstNonPendingIdx > 0) {
                          rows.push(
                            <tr key={`sep-${b.id}`} data-testid="bookings-history-divider">
                              <td colSpan="7" className="pt-4 pb-1">
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8A8175]">
                                  <span className="h-px flex-1 bg-[#B8912E]/20"></span>
                                  Ya gestionadas
                                  <span className="h-px flex-1 bg-[#B8912E]/20"></span>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                        rows.push(
                          <tr key={b.id} className={`border-t border-white/5 ${isPending(b) ? "bg-[#FFF9EB]/40" : ""}`}>
                            <td className="py-3">
                              <input
                                type="checkbox"
                                checked={selBookings.has(b.id)}
                                onChange={() => toggleSelBooking(b.id)}
                                data-testid={`bookings-check-${b.phone}`}
                              />
                            </td>
                            <td className="py-3">{b.name || <span className="text-[#6B6B6B]">—</span>}{b.bea_call_requested && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-[#78350F] bg-[#FEF3C7] border border-[#F59E0B] rounded-full px-2 py-0.5" title="Bea llama"><Phone size={9}/>Bea llama</span>}</td>
                            <td className="py-3">{b.start ? `${formatSpanishDate(b.start)} · ${formatTime(b.start)}` : "-"}</td>
                            <td className="py-3">{b.phone}</td>
                            <td className="py-3">
                              {b.status === "confirmed" && <span className="text-green-400">Confirmada</span>}
                              {b.status === "awaiting_review" && <span className="text-yellow-300">Pdte. revisar</span>}
                              {b.status === "pending" && <span className="text-[#6B6B6B]">Sin justificante</span>}
                              {b.status === "cancelled" && <span className="text-red-400">Cancelada</span>}
                              {b.status === "auto_deleted" && <span className="text-red-400">Auto-borrada</span>}
                              {b.status === "rejected" && <span className="text-red-400">Rechazada</span>}
                              {b.status === "rescheduled" && <span className="text-[#8A8175]">Reprogramada</span>}
                            </td>
                            <td className="py-3">
                              {b.has_receipt ? (
                                <a
                                  href="#"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    const res = await api.get(`/receipt/${b.id}`, { responseType: "blob" });
                                    window.open(URL.createObjectURL(res.data));
                                  }}
                                  className="gold text-xs underline">Ver</a>
                              ) : <span className="text-[#6B6B6B] text-xs">—</span>}
                              {/* Ronda 33.4 · Resincronizar evento en Google Calendar.
                                  Útil cuando Bea borró manualmente el evento del
                                  calendar y el registro interno quedó huérfano. */}
                              {!["cancelled","auto_deleted","rejected","rescheduled"].includes(b.status) && (
                                <button
                                  onClick={async () => {
                                    if (!confirm(`Recrear el evento de Google Calendar para la cita de ${b.name || b.phone} del ${b.start ? formatSpanishDate(b.start) : ""}?\n\nEsto es seguro: si el evento ya existe, no crea duplicado.`)) return;
                                    try {
                                      const { data } = await api.post(`/admin/bookings/${b.id}/resync-calendar`, {});
                                      if (data.recreated) {
                                        toast.success(`Evento recreado. Nuevo id: ${data.event_id.slice(0,8)}…`);
                                      } else {
                                        toast.info(data.reason || "El evento ya existía, no se ha duplicado.");
                                      }
                                      await refreshAll();
                                    } catch (err) {
                                      toast.error(err?.response?.data?.detail || "No se pudo resincronizar.");
                                    }
                                  }}
                                  data-testid={`resync-${b.phone}`}
                                  title="Recrear el evento en Google Calendar si el registro interno quedó huérfano"
                                  className="ml-2 text-xs underline text-[#8A8175] hover:text-[#B8912E]"
                                >
                                  Resync
                                </button>
                              )}
                            </td>
                            <td className="py-3 text-right whitespace-nowrap">
                              {!cancelledRow(b) && <CancelBookingButton b={b} onDone={refreshAll} />}
                              <button
                                className="p-2 rounded-lg text-[#B02A2A] hover:bg-[#B02A2A]/10 transition-colors"
                                data-testid={`bookings-delete-${b.phone}`}
                                title="Eliminar esta reserva de forma permanente"
                                onClick={() => setDeleteTarget(b)}
                              >
                                <Trash2 size={14}/>
                              </button>
                            </td>
                          </tr>
                        );
                      });
                      return rows;
                    })()}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === "failed" && (
          <div className="card" data-testid="failed-bookings-list">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={18} className="text-[#B8912E]"/>
              <div className="label-eyebrow">Reservas fallidas ({failedBookings.length})</div>
            </div>
            <p className="text-[#6B6B6B] text-sm mb-4">
              Reservas que la clienta pidió, pero cuyo evento no se pudo crear en Google Calendar tras 2 intentos.
              La clienta ha visto un mensaje amable con botón «Reintentar». Aquí puedes verlas y contactarlas manualmente si hiciera falta.
            </p>
            {failedBookings.length === 0 ? (
              <div className="text-sm text-[#6B6B6B] italic" data-testid="failed-empty">
                No hay reservas fallidas. Todo va como una seda.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-[#6B6B6B] border-b border-[#0A0A0A]/10">
                      <th className="py-2 pr-3">Fecha registro</th>
                      <th className="py-2 pr-3">Nombre</th>
                      <th className="py-2 pr-3">Teléfono</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Hueco solicitado</th>
                      <th className="py-2 pr-3">Error</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="failed-tbody">
                    {failedBookings.map((f) => (
                      <tr key={f.id} className="border-b border-[#0A0A0A]/5 align-top" data-testid={`failed-row-${f.id}`}>
                        <td className="py-3 pr-3 text-[#6B6B6B] whitespace-nowrap">
                          {f.created_at ? formatSpanishDate(f.created_at) : "—"}
                        </td>
                        <td className="py-3 pr-3 text-[#0A0A0A]">{f.name || "—"}</td>
                        <td className="py-3 pr-3 text-[#0A0A0A] whitespace-nowrap">{f.phone || "—"}</td>
                        <td className="py-3 pr-3 text-[#0A0A0A]">{f.client_email || <span className="text-[#6B6B6B] italic">sin email</span>}</td>
                        <td className="py-3 pr-3 text-[#0A0A0A] whitespace-nowrap">
                          {f.start ? `${formatSpanishDate(f.start)} · ${formatTime(f.start)}` : "—"}
                        </td>
                        <td className="py-3 pr-3 text-xs text-red-700 max-w-[280px] break-words">{f.error || "—"}</td>
                        <td className="py-3 pr-3 text-right">
                          <button
                            data-testid={`failed-dismiss-${f.id}`}
                            onClick={async () => {
                              if (!window.confirm("¿Descartar este registro? Se borra solo la traza; la clienta puede haber reintentado ya.")) return;
                              try {
                                await api.delete(`/admin/bookings/failed/${f.id}`);
                                setFailedBookings((prev) => prev.filter((x) => x.id !== f.id));
                                toast.success("Registro descartado.");
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "No se pudo descartar");
                              }
                            }}
                            className="btn-ghost text-xs inline-flex items-center gap-1"
                          >
                            <Trash2 size={12}/> Descartar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "emails" && (
          <div className="card" data-testid="emails-list">
            <div className="flex items-center gap-2 mb-2">
              <Mail size={18} className="text-[#B8912E]"/>
              <div className="label-eyebrow">Emails enviados a clientas ({emails.length})</div>
            </div>
            <p className="text-[#6B6B6B] text-sm mb-2">
              Registro de los emails automáticos que el sistema envía a las clientas: confirmaciones, recordatorios, avisos de justificante, cancelaciones, etc. Puedes ver cada email en su formato original y borrar los que ya hayas revisado.
            </p>
            {emailStartDate && (
              <p className="text-xs text-[#8A8175] italic mb-4" data-testid="emails-start-notice">
                El registro de emails empieza el {emailStartDate}. Los emails enviados antes de esa fecha no aparecen aquí.
              </p>
            )}
            {selEmail.size > 0 && (
              <div className="mb-3 flex items-center gap-2 bg-[#F5E7B8]/40 border border-[#B8912E]/30 rounded-lg px-3 py-2">
                <span className="text-xs text-[#0A0A0A]">{selEmail.size} seleccionado{selEmail.size === 1 ? '' : 's'}</span>
                <button
                  data-testid="emails-bulk-delete"
                  disabled={bulkBusy}
                  onClick={async () => {
                    if (!window.confirm(`¿Borrar ${selEmail.size} email${selEmail.size === 1 ? '' : 's'} del registro? Los emails ya se enviaron; solo se borra la traza.`)) return;
                    setBulkBusy(true);
                    try {
                      const ids = Array.from(selEmail);
                      const r = await api.post("/admin/emails/bulk-delete", { ids });
                      setEmails((prev) => prev.filter((e) => !selEmail.has(e.id)));
                      setSelEmail(new Set());
                      toast.success(`${r.data?.deleted || 0} email(s) borrado(s).`);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "No se pudo borrar");
                    }
                    setBulkBusy(false);
                  }}
                  className="btn-ghost text-xs inline-flex items-center gap-1 ml-auto"
                >
                  <Trash2 size={12}/> Borrar seleccionados
                </button>
                <button
                  onClick={() => setSelEmail(new Set())}
                  className="btn-ghost text-xs"
                >Cancelar</button>
              </div>
            )}
            {emails.length === 0 ? (
              <div className="text-sm text-[#6B6B6B] italic" data-testid="emails-empty">
                Todavía no hay emails registrados. Cuando el sistema envíe uno a una clienta aparecerá aquí.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-[#6B6B6B] border-b border-[#0A0A0A]/10">
                      <th className="py-2 pr-3 w-8">
                        <input
                          type="checkbox"
                          data-testid="emails-select-all"
                          checked={selEmail.size === emails.length && emails.length > 0}
                          onChange={(e) => {
                            setSelEmail(e.target.checked ? new Set(emails.map((x) => x.id)) : new Set());
                          }}
                        />
                      </th>
                      <th className="py-2 pr-3">Fecha</th>
                      <th className="py-2 pr-3">Clienta</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Tipo</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="emails-tbody">
                    {emails.map((e) => (
                      <tr key={e.id} className="border-b border-[#0A0A0A]/5 align-top" data-testid={`email-row-${e.id}`}>
                        <td className="py-3 pr-3">
                          <input
                            type="checkbox"
                            data-testid={`email-select-${e.id}`}
                            checked={selEmail.has(e.id)}
                            onChange={(ev) => {
                              const next = new Set(selEmail);
                              if (ev.target.checked) next.add(e.id); else next.delete(e.id);
                              setSelEmail(next);
                            }}
                          />
                        </td>
                        <td className="py-3 pr-3 text-[#6B6B6B] whitespace-nowrap">
                          {e.sent_at ? `${formatSpanishDate(e.sent_at)} · ${formatTime(e.sent_at)}` : "—"}
                        </td>
                        <td className="py-3 pr-3 text-[#0A0A0A]">
                          <div>{e.client_name || "—"}</div>
                          {e.client_phone && <div className="text-xs text-[#6B6B6B]">{e.client_phone}</div>}
                        </td>
                        <td className="py-3 pr-3 text-[#0A0A0A] truncate max-w-[220px]" title={e.to}>{e.to}</td>
                        <td className="py-3 pr-3">
                          <span className="inline-block text-xs bg-[#F5E7B8]/60 border border-[#B8912E]/30 rounded-full px-2 py-1">
                            {e.kind_label}
                          </span>
                          {e.delivered === false && (
                            <div className="text-xs text-red-700 mt-1">(no entregado)</div>
                          )}
                        </td>
                        <td className="py-3 pr-3 text-right whitespace-nowrap">
                          <button
                            data-testid={`email-view-${e.id}`}
                            onClick={async () => {
                              try {
                                const r = await api.get(`/admin/emails/${e.id}`);
                                setViewEmail(r.data);
                              } catch (err) {
                                toast.error("No se pudo cargar el email");
                              }
                            }}
                            className="btn-ghost text-xs inline-flex items-center gap-1 mr-1"
                          >
                            <Eye size={12}/> Ver
                          </button>
                          <button
                            data-testid={`email-delete-${e.id}`}
                            onClick={async () => {
                              if (!window.confirm("¿Borrar este email del registro?")) return;
                              try {
                                await api.delete(`/admin/emails/${e.id}`);
                                setEmails((prev) => prev.filter((x) => x.id !== e.id));
                                toast.success("Email borrado.");
                              } catch (err) {
                                toast.error("No se pudo borrar");
                              }
                            }}
                            className="btn-ghost text-xs inline-flex items-center gap-1"
                          >
                            <Trash2 size={12}/> Borrar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Ronda 33.19 · Modal de vista previa del email en iframe. */}
        {viewEmail && (
          <div
            data-testid="email-preview-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setViewEmail(null); }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[640px] max-h-[90vh] flex flex-col overflow-hidden">
              <div className="p-4 border-b border-[#0A0A0A]/10 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-[#6B6B6B]">
                    {viewEmail.kind_label} · {viewEmail.sent_at ? `${formatSpanishDate(viewEmail.sent_at)} · ${formatTime(viewEmail.sent_at)}` : ""}
                  </div>
                  <div className="font-serif text-lg text-[#0A0A0A] truncate">{viewEmail.subject}</div>
                  <div className="text-xs text-[#6B6B6B] truncate">
                    Para: {viewEmail.client_name ? `${viewEmail.client_name} · ` : ""}{viewEmail.to}
                  </div>
                </div>
                <button
                  onClick={() => setViewEmail(null)}
                  data-testid="email-preview-close"
                  aria-label="Cerrar"
                  className="w-8 h-8 rounded-full text-[#6B6B6B] hover:text-[#0A0A0A] hover:bg-[#F5E7B8]/30 flex items-center justify-center shrink-0"
                >
                  <X size={18}/>
                </button>
              </div>
              <iframe
                data-testid="email-preview-iframe"
                title="Vista previa del email"
                srcDoc={viewEmail.html || ""}
                className="flex-1 w-full min-h-[400px] bg-white"
                sandbox=""
              />
            </div>
          </div>
        )}




        {tab === "clients" && (
          <div className="card" data-testid="clients-list">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="label-eyebrow">Clientas ({bookings.length})</div>
                <p className="text-[#6B6B6B] text-sm mt-2">Todas las reservas registradas, con método de pago detectado y estado de pago.</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="clients-select-all">
                  <input
                    type="checkbox"
                    checked={bookings.length > 0 && bookings.every(b => selClients.has(b.id))}
                    onChange={(e) => {
                      if (e.target.checked) setSelClients(new Set(bookings.map(b => b.id)));
                      else setSelClients(new Set());
                    }}
                  />
                  Seleccionar todo
                </label>
                <button
                  onClick={() => bulkDeleteBookings(Array.from(selClients), "clients")}
                  disabled={bulkBusy || selClients.size === 0}
                  data-testid="clients-bulk-delete"
                  className="btn-ghost text-xs text-red-600 hover:text-red-800 disabled:opacity-40"
                >
                  <Trash2 size={12} className="inline mr-1"/>Eliminar seleccionadas ({selClients.size})
                </button>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[#8A8175] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="py-2 w-8"></th>
                    <th className="py-2">Nombre</th>
                    <th className="py-2">Teléfono</th>
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Método</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2 w-16 text-center">Notas</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.slice().sort((a,b) => (b.created_at||"").localeCompare(a.created_at||"")).map(b => {
                    const paid = b.status === "confirmed";
                    const cancelled = ["cancelled","auto_deleted","rejected","rescheduled"].includes(b.status);
                    const notesCount = (b.client_notes || []).length;
                    return (
                      <tr key={b.id} data-testid={`client-row-${b.phone}`} className="border-t border-[#0A0A0A]/8 hover:bg-[#FBF7EC]">
                        <td className="py-2">
                          <input
                            type="checkbox"
                            checked={selClients.has(b.id)}
                            onChange={() => toggleSelClient(b.id)}
                            data-testid={`clients-check-${b.phone}`}
                          />
                        </td>
                        <td className="py-2 text-[#0A0A0A]">
                          {b.name || "—"}
                          {b.bea_call_requested && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-[#78350F] bg-[#FEF3C7] border border-[#F59E0B] rounded-full px-2 py-0.5" title="Bea llama" data-testid={`bea-call-tag-${b.phone}`}>
                              <Phone size={9}/>Bea llama
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-[#0A0A0A]">{b.phone}</td>
                        <td className="py-2 text-[#0A0A0A]">{b.start ? formatSpanishDate(b.start) : "—"}</td>
                        <td className="py-2 text-[#0A0A0A]">{b.bizum_requested ? "Bizum" : "Transferencia"}</td>
                        <td className="py-2">
                          {cancelled && <span className="text-[#8A8175]">Cancelada</span>}
                          {!cancelled && paid && <span className="text-emerald-600 font-medium">Pagado</span>}
                          {!cancelled && !paid && b.has_receipt && <span className="text-amber-700 font-medium">Pdte. revisar</span>}
                          {!cancelled && !paid && !b.has_receipt && <span className="text-[#6B6B6B]">Sin pago</span>}
                        </td>
                        <td className="py-2 text-center">
                          {notesCount > 0 ? (
                            <button
                              onClick={() => setNotesTarget(b)}
                              data-testid={`view-notes-${b.phone}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#B8912E]/15 text-[#8F6F1F] font-medium text-xs hover:bg-[#B8912E]/25 transition-colors"
                              title="Ver notas de la clienta"
                            >
                              <StickyNote size={12}/> {notesCount}
                            </button>
                          ) : (
                            <span className="text-[#B8B4A5] text-xs">—</span>
                          )}
                        </td>
                        <td className="py-2 text-right whitespace-nowrap">
                          {!cancelledRow(b) && <CancelBookingButton b={b} onDone={refreshAll} />}
                          <button
                            className="p-2 rounded-lg text-[#B02A2A] hover:bg-[#B02A2A]/10 transition-colors"
                            data-testid={`delete-booking-${b.phone}`}
                            title="Eliminar esta reserva de forma permanente"
                            onClick={() => setDeleteTarget(b)}
                          >
                            <Trash2 size={14}/>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {bookings.length === 0 && (
                    <tr><td colSpan="8" className="py-6 text-center text-[#8A8175]">Aún no hay clientas registradas.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "content" && config && (
          <ContentEditor
            initial={config.content_texts || {}}
            onSaved={(patched) => setConfig((c) => ({ ...c, content_texts: patched }))}
          />
        )}

        {tab === "payment" && config && (
          <div className="space-y-6">
            <div className="card">
              <div className="label-eyebrow">Datos de pago (visibles para clientas)</div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <input className="field" data-testid="pay-holder" defaultValue={config.payment_info.account_holder}
                  onBlur={(e) => saveConfig({ payment_info: { ...config.payment_info, account_holder: e.target.value } })}
                  placeholder="Titular"/>
                <input className="field" data-testid="pay-iban" defaultValue={config.payment_info.iban}
                  onBlur={(e) => saveConfig({ payment_info: { ...config.payment_info, iban: e.target.value } })}
                  placeholder="IBAN"/>
                <input className="field" data-testid="pay-concept" defaultValue={config.payment_info.concept}
                  onBlur={(e) => saveConfig({ payment_info: { ...config.payment_info, concept: e.target.value } })}
                  placeholder="Concepto"/>
                <textarea className="field md:col-span-1" data-testid="pay-notes" defaultValue={config.payment_info.notes}
                  onBlur={(e) => saveConfig({ payment_info: { ...config.payment_info, notes: e.target.value } })}
                  placeholder="Notas para la clienta" rows={3}/>
              </div>
            </div>
            <div className="card">
              <div className="label-eyebrow">Contacto adicional</div>
              <p className="text-[#6B6B6B] text-sm mt-2">Se muestra a las clientas que no encuentran un horario libre.</p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <input className="field" data-testid="contact-email" defaultValue={config.contact_email || ""}
                  onBlur={(e) => saveConfig({ contact_email: e.target.value })}
                  placeholder="Tu email de contacto"/>
                <textarea className="field" data-testid="custom-hours-msg" defaultValue={config.custom_hours_message || ""}
                  onBlur={(e) => saveConfig({ custom_hours_message: e.target.value })}
                  placeholder="Mensaje que aparece si no encuentran horario" rows={3}/>
              </div>
            </div>
            <ChangePasswordCard />
          </div>
        )}
      </main>

      <DeleteBookingModal
        target={deleteTarget}
        loading={deleting}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          setDeleting(true);
          try {
            await api.delete(`/admin/bookings/${deleteTarget.id}`);
            setBookings((prev) => prev.filter((x) => x.id !== deleteTarget.id));
            toast.success("Reserva eliminada.");
            setDeleteTarget(null);
          } catch (e) {
            toast.error("No pude eliminar la reserva.");
          } finally {
            setDeleting(false);
          }
        }}
      />

      <NotesModal target={notesTarget} onClose={() => setNotesTarget(null)} />

      {tab === "chats" && chatsTab === "sol" && <div className="max-w-6xl mx-auto px-6 pb-12"><ChatHistoryModal
        inline
        open
        onClose={() => setChatOpen(false)}
        data={chatHistory}
        title="Conversaciones con Sol"
        variant="sol"
        selected={chatSel}
        onSelect={setChatSel}
        selectedKeys={selChat}
        onToggleKey={toggleSelChat}
        onSelectAll={(all) => setSelChat(new Set(all ? (chatHistory || []).map(s => s.key) : []))}
        onBulkDelete={() => bulkDeleteChat(Array.from(selChat))}
        bulkBusy={bulkBusy}
        onDelete={async (session) => {
          if (!session?.key) return;
          if (!window.confirm(`¿Eliminar toda la conversación${session.phone ? ` de ${session.phone}` : " anónima"}? Esta acción no se puede deshacer.`)) return;
          try {
            await api.delete(`/admin/chat-history/${encodeURIComponent(session.key)}`);
            const filtered = (chatHistory || []).filter((s) => s.key !== session.key);
            setChatHistory(filtered);
            if (chatSel?.key === session.key) setChatSel(null);
            toast.success("Conversación eliminada");
          } catch (e) {
            toast.error("No pude eliminar la conversación");
          }
        }}
      /></div>}

      {tab === "chats" && chatsTab === "booking" && <div className="max-w-6xl mx-auto px-6 pb-12"><ChatHistoryModal
        inline
        open
        onClose={() => setBookingChatOpen(false)}
        data={bookingChatHistory}
        selected={bookingChatSel}
        onSelect={setBookingChatSel}
        selectedKeys={selBookingChat}
        onToggleKey={toggleSelBookingChat}
        onSelectAll={(all) => setSelBookingChat(new Set(all ? (bookingChatHistory || []).map(s => s.key) : []))}
        onBulkDelete={() => bulkDeleteBookingChat(Array.from(selBookingChat))}
        bulkBusy={bulkBusy}
        title="Conversaciones de reservas"
        variant="booking"
        onDelete={async (session) => {
          if (!session?.key) return;
          if (!window.confirm(`¿Eliminar toda la conversación${session.phone ? ` de ${session.phone}` : " anónima"} del bot de reservas? Esta acción no se puede deshacer.`)) return;
          try {
            await api.delete(`/admin/booking-chat-history/${encodeURIComponent(session.key)}`);
            const filtered = (bookingChatHistory || []).filter((s) => s.key !== session.key);
            setBookingChatHistory(filtered);
            if (bookingChatSel?.key === session.key) setBookingChatSel(null);
            toast.success("Conversación eliminada");
          } catch (e) {
            toast.error("No pude eliminar la conversación");
          }
        }}
      /></div>}
    </div>
  );
}

function NotesModal({ target, onClose }) {
  const notes = target?.client_notes || [];
  return (
    <AnimatePresence>
      {target && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[65] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="notes-modal"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl border border-[#B8912E]/30"
          >
            <div className="p-5 border-b border-[#B8912E]/20 bg-[#FFF9EB] flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-[#8F6F1F]">
                  <StickyNote size={14}/>
                  <span className="label-eyebrow">Notas de la clienta</span>
                </div>
                <h3 className="font-serif text-xl text-[#0A0A0A] mt-1">{target.name || "Sin nombre"}</h3>
                <div className="text-xs text-[#6B6B6B] mt-0.5">
                  {target.phone}{target.start ? ` · ${new Date(target.start).toLocaleString("es-ES")}` : ""}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-[#6B6B6B] hover:bg-[#0A0A0A]/5"
                data-testid="notes-modal-close"
              >
                <X size={16}/>
              </button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto space-y-4">
              {notes.length === 0 && (
                <div className="text-sm text-[#6B6B6B]">Esta clienta aún no ha dejado ninguna nota.</div>
              )}
              {notes.map((n, i) => (
                <div key={i} className="p-4 rounded-xl bg-[#FBF7EC] border border-[#B8912E]/20" data-testid={`note-item-${i}`}>
                  <div className="text-[11px] text-[#6B6B6B] mb-2">
                    {n.created_at ? new Date(n.created_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : ""}
                  </div>
                  <p className="text-[15px] text-[#0A0A0A] leading-relaxed whitespace-pre-line italic">«{n.note}»</p>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PersonalCalendarPicker({ calendars, consultasId, value, onChange }) {
  const options = (calendars || []).filter((c) => c.id !== consultasId);
  const [diagnostic, setDiagnostic] = useState(null);
  const [checking, setChecking] = useState(false);

  const runDiagnostic = async () => {
    setChecking(true);
    setDiagnostic(null);
    try {
      const { data } = await api.post("/admin/calendar-diagnostic");
      setDiagnostic(data);
    } catch (e) {
      setDiagnostic({
        ok: false,
        detail: e?.response?.data?.detail || e?.message || "Error desconocido",
      });
    }
    setChecking(false);
  };

  const personalRow = (diagnostic?.calendars || []).find((c) => c.role === "personal");
  const hasError = diagnostic && (
    !diagnostic.ok || (personalRow && personalRow.reachable === false)
  );

  return (
    <div className="card" data-testid="personal-calendar-picker">
      <div className="label-eyebrow">Calendario personal (opcional)</div>
      <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">
        Antes de proponer un hueco, el sistema comprueba también este calendario para no ofrecer horarios que choquen con tus compromisos personales. Un evento en la mañana bloquea toda la mañana; un evento en la tarde bloquea toda la tarde.
      </p>
      <div className="mt-3 p-3 rounded-xl bg-[#FFF9EB] border border-[#B8912E]/25 text-xs text-[#0A0A0A] leading-relaxed">
        <strong>Privacidad:</strong> el sistema sólo consulta el estado <em>libre / ocupado</em> de este calendario (Google Free/Busy). Nunca lee títulos, descripciones ni ningún otro detalle de tus eventos personales.
      </div>
      <div className="mt-4">
        <select
          className="field"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid="personal-calendar-select"
        >
          <option value="">— Ninguno (solo consultas) —</option>
          {options.map((c) => (
            <option key={c.id} value={c.id}>{c.summary || c.id}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 pt-4 border-t border-[#0A0A0A]/10">
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={runDiagnostic}
          disabled={checking}
          data-testid="calendar-diagnostic-btn"
        >
          {checking ? "Comprobando…" : "Comprobar acceso a calendarios"}
        </button>
        {diagnostic && (
          <div
            className={`mt-3 p-3 rounded-xl text-xs leading-relaxed border ${
              hasError
                ? "bg-[#FDECEC] border-[#B02A2A]/40 text-[#7A1616]"
                : "bg-[#EAF6EC] border-[#2E7D32]/30 text-[#1B4D1F]"
            }`}
            data-testid="calendar-diagnostic-result"
          >
            {!diagnostic.ok && (
              <div>
                <strong>No se ha podido comprobar:</strong> {diagnostic.detail || diagnostic.reason}
                {diagnostic.reason === "insufficient_oauth_scope" && (
                  <div className="mt-3 p-3 rounded-lg bg-white/60 border border-[#B02A2A]/30">
                    <div className="font-medium mb-1">Cómo arreglarlo (solo tú puedes hacerlo)</div>
                    <ol className="list-decimal ml-4 space-y-1">
                      <li>Sube arriba al bloque «Google conectado» y pulsa <strong>«Desconectar»</strong>.</li>
                      <li>Vuelve a pulsar <strong>«Conectar Google»</strong>.</li>
                      <li>En la pantalla de Google, <strong>acepta todas las casillas</strong> (no desmarques ninguna).</li>
                      <li>Vuelve aquí y pulsa «Comprobar acceso a calendarios» de nuevo.</li>
                    </ol>
                    {diagnostic.granted_scope && (
                      <div className="mt-2 pt-2 border-t border-current/20 text-[10px] font-mono break-all opacity-75">
                        scope actual: {diagnostic.granted_scope}
                      </div>
                    )}
                  </div>
                )}
                {diagnostic.hint && <div className="mt-1">{diagnostic.hint}</div>}
              </div>
            )}
            {diagnostic.ok && (
              <div className="space-y-2">
                {(diagnostic.calendars || []).map((c) => (
                  <div key={c.calendar_id} className="flex flex-col">
                    <div>
                      <strong>{c.role === "personal" ? "Personal" : "Consultas"}:</strong>{" "}
                      <code className="text-[#0A0A0A]">{c.calendar_id}</code>
                    </div>
                    <div>
                      {c.reachable ? (
                        <>✓ accesible · {c.busy_count} ventanas ocupadas en los próximos 30 días</>
                      ) : (
                        <>✗ <strong>NO accesible</strong> · {JSON.stringify(c.errors)}</>
                      )}
                    </div>
                  </div>
                ))}
                {(diagnostic.warnings || []).map((w, i) => (
                  <div key={i} className="mt-2 pt-2 border-t border-current/20">
                    <strong>Aviso:</strong> {w}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DeleteBookingModal({ target, loading, onCancel, onConfirm }) {  return (
    <AnimatePresence>
      {target && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onCancel}
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="delete-booking-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl border border-[#B02A2A]/30"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-[#B02A2A]/10 text-[#B02A2A]">
                <AlertTriangle size={18}/>
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-xl text-[#0A0A0A]">Eliminar reserva</h3>
                <p className="text-sm text-[#6B6B6B] mt-2">
                  Vas a borrar de forma <strong>permanente</strong> esta reserva y también su evento del calendario de Google:
                </p>
                <div className="mt-3 p-3 rounded-xl bg-[#FBF7EC] border border-[#B8912E]/25 text-sm text-[#0A0A0A]">
                  <div><strong>{target.name || "—"}</strong></div>
                  <div className="text-[#6B6B6B] text-xs mt-1">{target.phone} · {target.start ? new Date(target.start).toLocaleString("es-ES") : "—"}</div>
                  <div className="text-[#6B6B6B] text-xs">Estado: {target.status}</div>
                </div>
                <p className="text-xs text-[#B02A2A] mt-3">Esta acción no se puede deshacer.</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2 rounded-full text-sm text-[#0A0A0A] border border-[#0A0A0A]/20 hover:bg-[#F7F5F0] transition-colors disabled:opacity-50"
                data-testid="delete-booking-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className="px-4 py-2 rounded-full text-sm font-medium bg-[#B02A2A] text-white hover:bg-[#8B1F1F] transition-colors disabled:opacity-50"
                data-testid="delete-booking-confirm"
              >
                {loading ? "Eliminando…" : "Sí, eliminar"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ContentEditor({ initial, onSaved }) {
  const [values, setValues] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);

  const SECTIONS = [
    {
      title: "Portada · Hero (bienvenida)",
      description: "El primer bloque que ve la clienta: eyebrow, titular en dos colores y párrafo intro.",
      fields: [
        { key: "landing_hero_eyebrow", label: "Eyebrow (línea pequeña arriba)", rows: 1 },
        { key: "landing_hero_headline_lead", label: "Titular · texto normal (antes del dorado)", rows: 1 },
        { key: "landing_hero_headline_gold", label: "Titular · texto DORADO en cursiva", rows: 1 },
        { key: "landing_hero_headline_tail", label: "Titular · segunda línea (debajo del dorado)", rows: 1 },
        { key: "landing_hero_intro", label: "Párrafo de introducción bajo el titular" },
      ],
    },
    {
      title: "Portada · Tarjeta «La consulta»",
      description: "Tarjeta dorada del hero. Los bullets van uno por línea.",
      fields: [
        { key: "landing_consultation_bullets", label: "Bullets — uno por línea" },
      ],
    },
    {
      title: "Portada · Servicios (tres voces)",
      description: "Bloque de tres tarjetas de servicios: eyebrow, titular partido y descripción interna de cada tarjeta.",
      fields: [
        { key: "landing_services_eyebrow", label: "Eyebrow (línea pequeña arriba)", rows: 1 },
        { key: "landing_services_heading_lead", label: "Titular · parte normal", rows: 1 },
        { key: "landing_services_heading_gold", label: "Titular · parte DORADA en cursiva", rows: 1 },
        { key: "landing_service_astrology_desc", label: "Tarjeta 1 · Astrología (texto interno)" },
        { key: "landing_service_numerology_desc", label: "Tarjeta 2 · Numerología (texto interno)" },
        { key: "landing_service_tarot_desc", label: "Tarjeta 3 · Tarot intuitivo (texto interno)" },
      ],
    },
    {
      title: "Portada · Quién soy",
      description: "Sección de biografía de Bea. Incluye la cita del libro y los 4 párrafos del texto.",
      fields: [
        { key: "landing_about_subtitle", label: "Subtítulo bajo el nombre (columna izquierda)" },
        { key: "landing_about_quote", label: "Cita del libro (aparece en cursiva con comillas grandes)", rows: 1 },
        { key: "landing_about_quote_attribution", label: "Atribución de la cita (aparece como «— …»)", rows: 1 },
        { key: "landing_about_p1", label: "Párrafo 1" },
        { key: "landing_about_p2", label: "Párrafo 2" },
        { key: "landing_about_p3", label: "Párrafo 3" },
        { key: "landing_about_p4", label: "Párrafo 4" },
        { key: "landing_about_gratitude", label: "Párrafo final de agradecimiento (en cursiva, más pequeño)" },
      ],
    },
    {
      title: "Portada · Caption del libro",
      description: "Texto en cursiva bajo la portada del libro «Un Lenguaje Invisible».",
      fields: [
        { key: "landing_book_caption", label: "Caption del libro" },
      ],
    },
    {
      title: "Landing · Mi enfoque",
      description: "Texto grande de la portada, seguido de los cuatro bloques de disciplinas.",
      fields: [
        { key: "landing_approach_heading", label: "Título de la sección", rows: 1 },
        { key: "landing_approach_intro", label: "Introducción" },
        { key: "landing_approach_astrology", label: "Astrología predictiva" },
        { key: "landing_approach_numerology", label: "Numerología pitagórica" },
        { key: "landing_approach_tarot", label: "Tarot intuitivo" },
        { key: "landing_approach_intuition", label: "Intuición" },
      ],
    },
    {
      title: "Landing · Feng Shui",
      description: "Bloque separado que aclara que el Feng Shui se gestiona por WhatsApp.",
      fields: [
        { key: "landing_fengshui_heading", label: "Título Feng Shui" },
        { key: "landing_fengshui_body", label: "Cuerpo Feng Shui" },
      ],
    },
    {
      title: "Modo vacaciones",
      description: "Texto que aparece al entrar en /reservar cuando el Modo Vacaciones está activo. Usa doble salto de línea para separar párrafos.",
      fields: [
        { key: "vacation_message", label: "Mensaje de vacaciones (multi-línea)", plain: true },
      ],
    },
    {
      title: "Consulta urgente",
      description: "Tarjeta destacada + modal con el equipo de confianza. Todos los textos del bloque son editables aquí.",
      fields: [
        { key: "urgency_title", label: "Título (tarjeta y modal)", plain: true },
        { key: "urgency_intro", label: "Texto introductorio de la tarjeta (breve, ~1-2 líneas)", plain: true },
        { key: "urgency_message", label: "Mensaje del modal: nombres de las tarotistas, tarifas orientativas y teléfono", plain: true },
        { key: "urgency_phone", label: "Teléfono del gabinete (formato internacional, ej. +34984988915) — usado en el botón «Llamar al gabinete»", plain: true },
      ],
    },
    {
      title: "ChatBot · saludo y mensajes fijos",
      description: "Textos que ve la clienta en el chat de dudas.",
      fields: [
        { key: "chatbot_greeting", label: "Saludo inicial" },
        { key: "chatbot_payment_message", label: "Mensaje sobre el pago" },
        { key: "chatbot_times_notice", label: "Aviso de tiempos" },
      ],
    },
    {
      title: "Footer y aviso legal",
      description: "Pie de página del landing y del ChatBot.",
      fields: [
        { key: "landing_footer_credit", label: "Crédito del footer (sin el año — se añade solo)" },
        { key: "landing_privacy_notice", label: "Aviso de privacidad (RGPD, 12 meses)" },
      ],
    },
  ];

  const setValue = (k, v) => {
    setValues((s) => ({ ...s, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Split payload: content_texts fields vs. plain config fields (urgency_title / urgency_message)
      const plainKeys = new Set(
        SECTIONS.flatMap((s) => s.fields.filter((f) => f.plain).map((f) => f.key))
      );
      const contentTexts = {};
      const patch = {};
      Object.entries(values).forEach(([k, v]) => {
        if (plainKeys.has(k)) patch[k] = v;
        else contentTexts[k] = v;
      });
      if (Object.keys(contentTexts).length) patch.content_texts = contentTexts;
      const { data } = await api.put("/admin/config", patch);
      onSaved(data.content_texts || {});
      setDirty(false);
      toast.success("Textos guardados. Ya se ven en la web.");
    } catch (e) {
      toast.error("No pude guardar los textos.");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="content-editor">
      <div className="card border-[#B8912E]/25">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <PenLine size={12} className="text-[#B8912E]"/> Editor de textos
            </div>
            <h2 className="font-serif text-2xl mt-2 text-[#0A0A0A]">Cambia lo que quieras y publica</h2>
            <p className="text-sm text-[#6B6B6B] mt-2 max-w-lg">
              Todo lo que escribas aquí se guarda directamente en base de datos. En cuanto pulses «Guardar», la web se actualiza sin necesidad de desplegar nada.
            </p>
          </div>
          <button
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="content-save-btn"
          >
            <Save size={14}/> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>

      {SECTIONS.map((section) => (
        <div key={section.title} className="card">
          <div className="label-eyebrow">{section.title}</div>
          <p className="text-xs text-[#6B6B6B] mt-2">{section.description}</p>
          <div className="mt-4 space-y-4">
            {section.fields.map((f) => {
              const current = values[f.key] ?? initial[f.key] ?? "";
              const isSingle = f.rows === 1;
              return (
                <div key={f.key} data-testid={`content-field-${f.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-[#0A0A0A]">{f.label}</label>
                    <button
                      type="button"
                      onClick={() => setPreviewKey(previewKey === f.key ? null : f.key)}
                      className="text-[11px] text-[#B8912E] inline-flex items-center gap-1 hover:underline"
                      data-testid={`content-preview-${f.key}`}
                    >
                      <Eye size={12}/> {previewKey === f.key ? "Ocultar preview" : "Previsualizar"}
                    </button>
                  </div>
                  {isSingle ? (
                    <input
                      className="field"
                      value={current}
                      onChange={(e) => setValue(f.key, e.target.value)}
                      data-testid={`content-input-${f.key}`}
                    />
                  ) : (
                    <textarea
                      className="field"
                      rows={4}
                      value={current}
                      onChange={(e) => setValue(f.key, e.target.value)}
                      data-testid={`content-input-${f.key}`}
                    />
                  )}
                  {previewKey === f.key && (
                    <div className="mt-2 p-4 rounded-xl border border-[#B8912E]/30 bg-[#FFF9EB]" data-testid={`content-preview-box-${f.key}`}>
                      <div className="text-[10px] uppercase tracking-widest text-[#B8912E] mb-2">Vista previa</div>
                      <p className="text-sm text-[#0A0A0A] whitespace-pre-line font-serif italic">{current || "(vacío)"}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="card border-[#B8912E]/25 bg-[#FFF9EB]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-[#0A0A0A]">
            {dirty ? "Tienes cambios sin guardar." : "Sin cambios pendientes."}
          </div>
          <button
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="content-save-btn-bottom"
          >
            <Save size={14}/> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

const cancelledRow = (b) => ["cancelled", "auto_deleted", "rejected", "rescheduled"].includes(b.status);

function CancelBookingButton({ b, onDone, wide = false }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const reason = window.prompt(`Cancelar la cita de ${b.name || b.phone}. El evento queda como «cancelado» y el hueco vuelve a estar libre. Se envía el email de «Cita cancelada» a ti y a la clienta (si dejó email).\n\nMotivo (opcional):`);
    if (reason === null) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/cancel`, { reason });
      toast.success(data.calendar_updated ? "Cita cancelada y hueco liberado." : "Cita cancelada. No se pudo actualizar Google Calendar: revísalo.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cancelar"); }
    setBusy(false);
  };
  return (
    <button onClick={run} disabled={busy} data-testid={`cancel-booking-${b.id}`}
      className={`btn-ghost text-xs text-[#B02A2A] ${wide ? "w-full sm:w-auto justify-center flex items-center gap-2" : "mr-1"}`}>
      {busy ? "Cancelando…" : "Cancelar cita"}
    </button>
  );
}

function ForgotPasswordLink() {
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const ask = async () => {
    setState("sending");
    try {
      const { data } = await api.post("/admin/forgot-password");
      setMsg(data.message);
    } catch (e) {
      setMsg("No he podido enviar el enlace ahora mismo. Prueba en unos minutos.");
    }
    setState("done");
  };
  if (state === "done") return <p className="text-xs text-[#6B6B6B] mt-4" data-testid="forgot-password-message">{msg}</p>;
  return (
    <button type="button" onClick={ask} disabled={state === "sending"} data-testid="forgot-password-link"
      className="block mx-auto mt-4 text-xs text-[#6B6B6B] underline hover:text-[#0A0A0A] transition-colors">
      {state === "sending" ? "Enviando…" : "¿Has olvidado tu contraseña?"}
    </button>
  );
}

function ChatHistoryModal({ open, onClose, data, selected, onSelect, onDelete, selectedKeys, onToggleKey, onSelectAll, onBulkDelete, bulkBusy, title, variant = "sol", inline = false }) {
  const isBooking = variant === "booking";
  const allSelected = data && data.length > 0 && data.every(s => selectedKeys?.has(s.key));
  const selCount = selectedKeys?.size || 0;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={inline ? undefined : onClose}
          className={inline ? "mt-2" : "fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"}
          data-testid={inline ? `chat-history-panel-${variant}` : "chat-history-modal"}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-[#B8912E]/25 rounded-2xl w-full max-w-4xl h-[80vh] overflow-hidden flex flex-col shadow-2xl"
          >
            <div className={`p-4 border-b border-[#B8912E]/20 flex items-center justify-between gap-3 flex-wrap ${isBooking ? "bg-[#EEF4EA]" : "bg-[#FFF9EB]"}`} data-testid={`chat-history-header-${variant}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${isBooking ? "bg-[#3F6B3A] text-white" : "bg-[#B8912E] text-white"}`} data-testid={`chat-history-badge-${variant}`}>
                    {isBooking ? "Chat de reservas" : "Sol"}
                  </span>
                  <div className="font-serif text-2xl gold">{title || "Historial del chatbot"}</div>
                </div>
                <div className="text-xs text-[#6B6B6B] mt-1">
                  {isBooking
                    ? "Conversaciones completas del chat de reservas: fecha, hora, pago y dudas escritas durante la reserva."
                    : "Conversaciones con Sol en la web (anónimas o vinculadas a un teléfono). No incluye lo escrito dentro del chat de reservas."}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {data && data.length > 0 && (
                  <>
                    <label className="text-xs text-[#6B6B6B] flex items-center gap-2 cursor-pointer" data-testid="chat-select-all">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
                      />
                      Seleccionar todo
                    </label>
                    <button
                      onClick={onBulkDelete}
                      disabled={bulkBusy || selCount === 0}
                      data-testid="chat-bulk-delete"
                      className="btn-ghost text-xs text-red-600 hover:text-red-800 disabled:opacity-40"
                    >
                      <Trash2 size={12} className="inline mr-1"/>Eliminar ({selCount})
                    </button>
                  </>
                )}
                {!inline && <button onClick={onClose} className="btn-ghost text-xs" data-testid="chat-history-close">
                  <X size={14} className="inline mr-1"/>Cerrar
                </button>}
              </div>
            </div>
            <div className="flex-1 flex overflow-hidden">
              <div className="w-72 border-r border-[#B8912E]/15 overflow-y-auto shrink-0 bg-[#FBF7EC]">
                {!data && <div className="p-4 text-sm text-[#6B6B6B]">Cargando…</div>}
                {data && data.length === 0 && <div className="p-4 text-sm text-[#6B6B6B]">Sin conversaciones todavía.</div>}
                {data && data.map((s) => {
                  const active = selected?.key === s.key;
                  const checked = selectedKeys?.has(s.key);
                  return (
                    <div
                      key={s.key}
                      className={`w-full border-b border-[#B8912E]/10 transition-colors ${active ? "bg-[#B8912E]/15" : "hover:bg-white"}`}
                    >
                      <div className="flex items-center gap-2 px-3 py-3">
                        <input
                          type="checkbox"
                          checked={!!checked}
                          onChange={() => onToggleKey && onToggleKey(s.key)}
                          data-testid={`chat-check-${s.phone || s.session_id}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          onClick={() => onSelect(s)}
                          className="block flex-1 text-left"
                          data-testid={`chat-session-${s.phone || s.session_id}`}
                        >
                          <div className="flex items-center gap-2 text-sm">
                            {s.phone
                              ? <><Phone size={12} className="text-[#B8912E]"/> <span className="text-[#0A0A0A] font-medium">{s.client_name ? `${s.client_name} · ` : ""}{s.phone}</span></>
                              : <><span className="text-[#6B6B6B]">{s.client_name || "Anónima"}</span></>
                            }
                          </div>
                          <div className="text-[10px] text-[#6B6B6B] mt-1">{new Date(s.last_seen).toLocaleString("es-ES")}</div>
                          {(s.client_name || s.client_city) && (
                            <div className="text-[10px] mt-1 flex flex-wrap gap-1" data-testid={`chat-meta-${s.session_id || s.phone}`}>
                              {s.client_name && <span className="px-1.5 py-0.5 rounded-full bg-[#FEF3C7] border border-[#F59E0B] text-[#78350F] font-medium">{s.client_name}</span>}
                              {s.client_city && <span className="px-1.5 py-0.5 rounded-full bg-[#E0F2FE] border border-[#0EA5E9] text-[#0C4A6E] font-medium">{s.client_city}</span>}
                            </div>
                          )}
                          <div className="text-[10px] text-[#8A8175] mt-0.5">{s.messages.length} mensajes</div>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete && onDelete(s); }}
                          className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                          title="Eliminar esta conversación"
                          data-testid={`chat-session-delete-${s.phone || s.session_id}`}
                        >
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-white">
                {!selected && data && data.length > 0 && (
                  <div className="text-sm text-[#6B6B6B]">Selecciona una conversación a la izquierda.</div>
                )}
                {selected && selected.messages.map((m, i) => (m.role ? (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "bg-[#EEF4EA] border border-[#3F6B3A]/25 text-[#0A0A0A]"}`}>
                      {m.created_at && <div className={`text-[10px] mb-1 ${m.role === "user" ? "text-white/60" : "text-[#6B6B6B]"}`}>{new Date(m.created_at).toLocaleString("es-ES")}</div>}
                      {m.text}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="space-y-2" data-testid={`chat-msg-${i}`}>
                    <div className="flex justify-end">
                      <div className="max-w-[80%] px-3 py-2 rounded-2xl text-sm bg-[#0A0A0A] text-white">
                        <div className="text-[10px] text-white/60 mb-1">{new Date(m.created_at).toLocaleString("es-ES")}</div>
                        {m.user}
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="max-w-[80%] px-3 py-2 rounded-2xl text-sm bg-[#FFF7E5] border border-[#B8912E]/25 text-[#0A0A0A] whitespace-pre-line">
                        {m.assistant}
                      </div>
                    </div>
                  </div>
                )))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!current || !next) { toast.error("Rellena ambas contraseñas."); return; }
    if (next.length < 8) { toast.error("La nueva contraseña debe tener al menos 8 caracteres."); return; }
    if (next !== confirm) { toast.error("Las dos nuevas contraseñas no coinciden."); return; }
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next });
      toast.success("Contraseña actualizada. Úsala la próxima vez que entres.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar la contraseña.");
    }
    setSaving(false);
  };

  return (
    <div className="card" data-testid="change-password-card">
      <div className="label-eyebrow">Cambiar contraseña del panel</div>
      <p className="text-[#6B6B6B] text-sm mt-2">Solo tú tienes acceso a este panel. Cámbiala cuando quieras (mínimo 8 caracteres).</p>
      <div className="grid md:grid-cols-3 gap-3 mt-4">
        <input type="password" className="field" data-testid="pwd-current" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Contraseña actual" autoComplete="current-password"/>
        <input type="password" className="field" data-testid="pwd-new" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Nueva contraseña" autoComplete="new-password"/>
        <input type="password" className="field" data-testid="pwd-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repite la nueva" autoComplete="new-password"/>
      </div>
      <button onClick={submit} disabled={saving} data-testid="pwd-submit" className="btn-primary text-sm mt-4">
        {saving ? "Guardando..." : "Cambiar contraseña"}
      </button>
    </div>
  );
}

function ScheduleDayEditor({ day, idx, ranges, onChange }) {
  const [local, setLocal] = useState(ranges);
  useEffect(() => { setLocal(ranges); }, [ranges]);
  const updateAt = (i, key, val) => {
    const next = local.map((r, j) => j === i ? { ...r, [key]: val } : r);
    setLocal(next);
  };
  const commit = () => onChange(local);
  return (
    <div className="card" data-testid={`schedule-day-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="font-serif text-xl">{day}</div>
        <button onClick={() => { const n = [...local, { start: "10:00", end: "11:00" }]; setLocal(n); onChange(n); }} className="btn-ghost text-xs">+ Franja</button>
      </div>
      {local.length === 0 && <p className="text-[#6B6B6B] text-sm mt-3">Sin franjas.</p>}
      {local.map((r, i) => (
        <div key={i} className="mt-3 flex items-center gap-2">
          <input type="time" className="field" style={{ maxWidth: "130px" }} value={r.start} onChange={(e) => updateAt(i, "start", e.target.value)} onBlur={commit}/>
          <span className="text-[#6B6B6B]">→</span>
          <input type="time" className="field" style={{ maxWidth: "130px" }} value={r.end} onChange={(e) => updateAt(i, "end", e.target.value)} onBlur={commit}/>
          <button className="btn-ghost text-xs" onClick={() => { const n = local.filter((_, j) => j !== i); setLocal(n); onChange(n); }}>Quitar</button>
        </div>
      ))}
    </div>
  );
}
